import { Price } from "./Price";
import Utils from "../portlibs/PortUtils";
import Holding from "./Holding";
import createHolding from "./HoldingFactory"
import _ from "lodash";
import CashHolding from "./CashHolding";
import { EAccountTypes, CashEqRegex, IHolding, IValue } from "../shared/Consts";
import IPortfolio from "../shared/IPortfolio";
import SummaryHolding from "./SummaryHolding";
import Prices from "./Prices";
import PortUtils from "../portlibs/PortUtils";

// company overview
export interface IOverview {
    name:string,
    div:number,         // last diviend payout
    rate:number,        // dividend rate in yield %
    yield:number,       // dividend yearly yield $
}

export interface IAccountClient {
    id:number;
    name:string;
    type:EAccountTypes
    hide?:boolean
}

export default class Portfolio {
    private _id:string;
    private _tickers:string[] = [];
    private _holdings:Map<string,Holding> = new Map();
    private _cash:Map<string,Holding> = new Map();
    private _all:Map<string,Holding> = new Map();
    private _accounts : IAccountClient[] = [];
    private _accountIds : {[key:number]:string} = {}
    private _json:any = {}

    get userId(){return this._id}
    get json(){return this._json}

    constructor(json?:IPortfolio){
        if (json) {
            this._id = json._id;
            this._json = json
            
            this.setAccounts(json.accounts)
            for(const key in json.holdings){
                const holding = createHolding(this, key, json.holdings[key]); 
                this._all.set(key, holding);
                if (Price.isCash(key)) {
                    this._cash.set(key, holding)
                }
                else {
                    this._tickers.push(key);
                    this._holdings.set(key, holding)
                }
            }
        }    
        else {
            this._id = ""
        }
        if (typeof(window)!="undefined") (window as any).user = this
    }
    get accounts(){return this._accounts}
    get holdings(){return this._holdings.values()}
    account(accountId:number) { return this._accounts.find(a => a.id==accountId) }
    accountName(accountId:number) { return this._accountIds[accountId] || "Unknown" }
    
    // get currentAccount(){
    //     const accountId = Path.i.account;
    //     if (accountId<0) return this.accounts[0].id;
    //     else return accountId;
    // }
    private setAccounts(json:any) {
        this._accounts.push({id:0, name:"CASH", type:EAccountTypes.cash})
        this._accounts.push({id:1, name:"RRSP", type:EAccountTypes.rrsp})
        this._accounts.push({id:2, name:"TFSA", type:EAccountTypes.tfsa})
        for(let id in json){
            const row = json[id];
            const accountId = Number(id)
            if (_.findIndex(this._accounts, acc => acc.id==accountId)>=0) continue;
            this._accounts.push({id:accountId, name:row.n, type:row.t||EAccountTypes.cash, hide:row.h})
        }
        this._accounts.sort((l,r) => {
            if (l.type==r.type) return l.name.localeCompare(r.name)
            else return l.type-r.type
        })
        this.syncIds();
    }
    private syncIds(){
        for(let row of this._accounts) {
            this._accountIds[row.id] = row.name;
        }
    }

    get stockTickers() : Array<string> {
        return this._tickers.filter(ticker => !Price.isCashEq(ticker) && !CashEqRegex.test(ticker))
    }

    filter(currency:number, search:string, account:number, summarizeFixed:boolean) : Holding[] {
        search = search.toLowerCase()
        if (search[0]=="=") {
            const holding = this.holding(search.substring(1).toUpperCase(), true)
            if (!holding) return []
            else if ( (currency<0 || holding?.currency==currency) ) return account<0 ? [holding] : [holding.filterByAccount(account)]
            else return []
        }

        const ret:Holding[] = []
        for(const holding of this._holdings.values()) {
            if (currency>=0 && holding.currency!=currency) continue
            else if (search && !holding.shouldFilter(search)) continue
            else if (PortUtils.isGICHolding(holding) && summarizeFixed) {}
            else if (account<0) holding.transactions.length && ret.push(holding)
            else {
                const filtered = holding.filterByAccount(account)
                if (filtered.transactions.length) ret.push(filtered)
            }
        }
        if (summarizeFixed) {
            if (currency<0 || currency==0) {
                this.appendFixed(ret, "GIC", 0, search, account)
                this.appendFixed(ret, "BOND", 0, search, account)
                this.appendFixed(ret, "SAVE", 0, search, account)
            }
            if (currency<0 || currency==1) {
                this.appendFixed(ret, "GIC", 1, search, account)
                this.appendFixed(ret, "BOND", 1, search, account)
                this.appendFixed(ret, "SAVE", 1, search, account)
            }
        }
        return ret
    }

    private appendFixed(ret:Holding[], type:"GIC"|"BOND"|"SAVE", currency:number, search:string, account:number) {
        const holding = this.termHolding(type, currency, search, account)
        if (holding.quantity) ret.push(holding)
    }

    termHolding(type:"GIC"|"SAVE"|"BOND", currency:number, search:string, account:number) {
        return new SummaryHolding(this, type + (currency==1 ? ".US" : ""), this.termHoldings(type, currency, search, account))
    }

    cash(currency:number, accountId:number=-1) {
        const symbol = currency ? "CASH.US" : "CASH"
        const holding = (this._cash.get(symbol) || createHolding(this, symbol, [])) as CashHolding
        if (accountId>=0) return holding.filterByAccount(accountId)
        else return holding
    }
    holding(ticker:string, isStrict?:boolean) : Holding|null {
        if (Price.isCash(ticker)) return this.cash(ticker.match(/\.US$/) ? 1 : 0)!
        else if (this._holdings.has(ticker)) return this._holdings.get(ticker)!
        else if (!isStrict) return createHolding(this, ticker, [])
        else return null
    }
    termQuantity(type:"GIC"|"SAVE"|"BOND",market:number,search:string="",accountId:number=-1,endTime:number=0) {
        let holdings = this.termHoldings(type, market, search, accountId)
        if (endTime) holdings = PortUtils.trimHoldings(holdings, endTime)
        return holdings.reduce((p,holding) => p+holding.quantity, 0)
    }
    termHoldings(type:"GIC"|"SAVE"|"BOND"|null,market:number,search:string="",accountId:number=-1) {
        const prefix = type ? new RegExp(`^${type}\-${market ? "US" : "CA"}-`) : CashEqRegex
        return Array.from(this._holdings.values()).filter(holding => 
            prefix.test(holding.id) && 
            (!search || holding.shouldFilter(search)) && 
            (accountId<0 || (holding.transactions.length>0 && holding.transactions[0].accountId==accountId)))
    }
    holdingValue(currency:number, search:string, accountId:number, excludeCash:boolean=false,endTime:number=0){
        let total = 0;
        for(let holding of this._all.values()){
            if (holding.currency==currency && holding.shouldFilter(search)) {
                if (!excludeCash || !holding.isCashEq) {
                    holding = holding.filterByAccount(accountId)
                    if (endTime) holding = PortUtils.trimTransactions(holding.filterByAccount(accountId), endTime)
                    total += holding.holdingValue
                }
            }
        }
        return total;
    }
    totalValue(accountId:number){
        let total = 0;
        for(let holding of this._all.values()){
            const exchange = holding.currency ? Prices.i.USD2CAD : 1
            total += (holding.filterByAccount(accountId)?.holdingValue||0) * exchange;
        }
        return Utils.formatCurrency(0, total);
    }

    // find all the "deposits" in an account (to calculate money weighted performance)
    getDeposit(accountId:number,currency:number,endTime:number=0){
        let holding : CashHolding = this.cash(currency).filterByAccount(accountId) as CashHolding
        if (endTime) holding = PortUtils.trimTransactions(holding, endTime) as CashHolding
        return holding.totalDeposits
    }
    getDeposits(accountId:number,currencyFilter:number){
        if (currencyFilter==1) return this.getDeposit(accountId, 1)
        else return this.getDeposit(accountId, 0) + this.getDeposit(accountId, 1)*Prices.i.USD2CAD
    }

    static buildTotal(currency:number,holdings:Holding[],deposit:number) {
        let holdingCost = 0, holdingValue = 0, dividend = 0, profit = 0, gain = 0, ugain = 0, a_payout = 0;
        for(let row of holdings){
            const exchange = currency<0 ? row.exchange : 1;
            holdingCost += row.holdingCost * exchange;
            holdingValue += row.holdingValue * exchange;
            dividend += row.dividend * exchange;
            profit += row.profit * exchange;
            gain += row.gain * exchange;
            ugain += row.ugain * exchange;
            a_payout += row.a_payout * exchange
        }
        const holdingPercent = holdingCost ? (holdingValue-holdingCost)/holdingCost * 100 : 0;
        const percent = holdingCost ? (holdingValue-holdingCost+profit)/holdingCost * 100 : 0
        const mwPercent = deposit ? (holdingValue-deposit) / deposit * 100 : percent
        const value = (col:string) => {
            return col=="name" ? "\u2800" :  PortUtils.cellValue(col, (ret as any)[col], {currency:0})
        }
        const ret : IHolding&IValue = {
            id:"summary",holdingCost,holdingValue,dividend,a_payout,yield:a_payout/holdingValue*100,profit,gain,mwPercent,holdingPercent,currency:currency<0 ? 0 : currency,ugain, value
        }
        return ret
    }
}